import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateOrderItemDto, PromocodeType } from '@/app/store/api/restApi';
import { useCalculateDiscountMutation, useFindPromocodeMutation } from '@/entities';
import { setPromocode, setPromoLoading, useAppDispatch, useAppSelector } from '@/app/store';

export const useSetPromocode = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('promocodes');
  const [findPromocode] = useFindPromocodeMutation();
  const [calculateDiscount] = useCalculateDiscountMutation();
  const { products } = useAppSelector((state) => state.basket);

  const checkAndSetPromocode = useCallback(
    async (code: string) => {
      let resultText = '';

      try {
        dispatch(setPromoLoading(true));
        dispatch(setPromocode(undefined));
        const result = await findPromocode({ findPromocodeDto: { code } }).unwrap();

        if (
          !result.data ||
          result.data.isActive === false ||
          (result.data.maxUses && result.data.currentUses >= result.data.maxUses) ||
          (result.data.maxUsesPerUser && result.data.currentClientUses >= result.data.maxUsesPerUser) ||
          (result.data.validityPeriod && new Date(result.data.validityPeriod.begin) > new Date()) ||
          (result.data.validityPeriod && new Date(result.data.validityPeriod.end) < new Date())
        ) {
          if (result.data?.validityPeriod && new Date(result.data.validityPeriod.end) < new Date())
            resultText = t('errors.hasExpired');
          else if (result.data?.maxUses && result.data.currentUses >= result.data.maxUses)
            resultText = t('errors.usageLimit');
          else if (result.data?.maxUsesPerUser && result.data.currentClientUses >= result.data.maxUsesPerUser)
            resultText = t('errors.perUserLimit');
          else if (result.data?.isActivated === false) resultText = t('errors.isInvalid');
          else resultText = t('errors.applyFail');
        } else {
          resultText = '';
          const promocode = result.data;

          if (promocode.type === PromocodeType.OrderFreeDelivery)
            dispatch(setPromocode({ promocode, discount: undefined }));
          else {
            const orderedItems: CreateOrderItemDto[] = [];

            products.forEach((mi) => {
              if (mi.variants)
                mi.variants.forEach((vrnt) => {
                  if (vrnt.quantity > 0)
                    orderedItems.push({ menuItemId: mi.product.id, variantId: vrnt.value.id, quantity: vrnt.quantity });
                });
              else orderedItems.push({ menuItemId: mi.product.id, quantity: mi.quantity });
            });

            if (orderedItems.length === 0) dispatch(setPromocode({ promocode, discount: undefined }));
            else {
              const discount = await calculateDiscount({
                calculatePromocodeDiscountDto: { orderedItems, type: promocode.type, discount: promocode.discount },
              }).unwrap();

              dispatch(setPromocode({ promocode, discount: discount.data }));
            }
          }
        }
      } catch {
        resultText = t('errors.applyFail');
      } finally {
        dispatch(setPromoLoading(false));
      }

      return resultText;
    },
    [calculateDiscount, dispatch, findPromocode, products, t],
  );

  return { checkAndSetPromocode };
};
